import { Artboard } from '@/components/builder/artboard';
import { AssetsPanel } from '@/components/builder/assets-panel';
import { CommandPalette } from '@/components/command-palette';
import { BuilderSidebar } from '@/components/builder/builder-sidebar';
import type { BlockPreset } from '@/components/builder/drag-data';
import { canWrapNode } from '@/components/builder/node-actions';
import { iconDefaults, materializePreset, trackableScroll } from '@/components/builder/palette-presets';
import { IconPicker } from '@/components/builder/icon-picker';
import { Inspector } from '@/components/builder/inspector';
import { FloatingPalette, insertionPoint } from '@/components/builder/palette';
import { PreflightPanel } from '@/components/builder/preflight-panel';
import { PreviewDialog } from '@/components/builder/preview-dialog';
import { ShortcutReference, ShortcutReferenceButton } from '@/components/builder/shortcut-reference';
import { PasteReviewDialog } from '@/components/builder/paste-review-dialog';
import { StepsStrip } from '@/components/builder/steps-strip';
import { VariantSwitcher, type VariantDialogRequest } from '@/components/builder/variant-switcher';
import { ThemePanel } from '@/components/builder/theme-panel';
import { ThemeTokenNavigationProvider, type ThemeTokenFamily } from '@/components/builder/token-navigation';
import { useAutosave, type SaveState } from '@/components/builder/use-autosave';
import { ViewportToolbar } from '@/components/builder/viewport-toolbar';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/cn';
import { readEditorClipboard, readInternalEditorClipboard, writeEditorClipboard } from '@/lib/editor-clipboard';
import {
  browserBuilderPreferenceStorage,
  readBuilderPreferences,
  writeBuilderPreferences,
  type BuilderPanelId,
} from '@/lib/builder-preferences';
import {
  commandById,
  dispatchEditorKeydown,
  formatKeybinding,
  runEditorCommand,
  type EditorCommandContext,
  type EditorCommandId,
} from '@/lib/editor-commands';
import {
  useAppTheme,
  useAssets,
  useCampaigns,
  useContextTree,
  useExperience,
  useSetAppTheme,
  useUploadAsset,
} from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import {
  activeVariantKey,
  addStep,
  applyClipboardPaste,
  captureClipboardEnvelope,
  createSession,
  createVariant,
  deleteVariant,
  docStats,
  duplicateNodes,
  findNode,
  findStep,
  insertNode,
  isContainer,
  MAX_VARIANTS,
  moveNodes,
  moveStep,
  nodeLabel,
  normalizeTopLevelSelection,
  planClipboardPaste,
  removeNodes,
  removeStep,
  renameVariant,
  setLabel,
  stepsOf,
  stepToExperienceDocument,
  wrapNodes,
  type AuthoringDoc,
  type Frame as SolvedFrame,
  type SessionStore,
} from '@personalizehq/editor';
import { MAX_LAYOUT_NODES, type EditorClipboardEnvelope } from '@personalizehq/schemas';
import type { Viewport } from '@personalizehq/schemas/ir';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Frame, Redo2, Rocket, Smartphone, Undo2, ZoomIn, ZoomOut } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { toast } from 'sonner';

export const Route = createFileRoute('/_canvas/experiences/$id/edit')({ component: Builder });

function Builder(): React.JSX.Element {
  const { id } = Route.useParams();
  const { data: experience, isLoading } = useExperience(id);

  if (isLoading || !experience) {
    return <div className="grid h-screen place-items-center bg-zinc-900 text-sm text-zinc-400">Loading…</div>;
  }

  // `key` remounts the session when the experience changes: the store is seeded from the doc once
  // and owns it from then on (which is why the query is `staleTime: Infinity` — a refetch swapping
  // the doc out from under an open canvas would silently discard edits).
  return (
    <BuilderShell
      key={experience.id}
      experienceId={experience.id}
      name={experience.name}
      document={experience.document}
    />
  );
}

const DEFAULT_VIEWPORT: Viewport = { w: 390, h: 844, color_scheme: 'light', font_scale: 1, direction: 'ltr' };

function BuilderShell({
  experienceId,
  name,
  document: initialDoc,
}: {
  experienceId: string;
  name: string;
  document: AuthoringDoc;
}): React.JSX.Element {
  const { appId, environmentId } = useContextStore();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { data: orgs } = useContextTree();
  const { data: campaigns } = useCampaigns(appId);
  const {
    data: assets,
    error: assetsError,
    isError: assetsFailed,
    isPending: assetsPending,
    refetch: refetchAssets,
  } = useAssets(appId);
  const { data: appTheme } = useAppTheme(appId);
  const saveAppTheme = useSetAppTheme(appId);
  const [session] = useState<SessionStore>(() => createSession(initialDoc));

  const doc = useStore(session, (s) => s.doc);
  const selection = useStore(session, (s) => s.selection);
  const selectionAnchor = useStore(session, (s) => s.selectionAnchor);
  const focusedStepId = useStore(session, (s) => s.focusedStepId);

  const [initialPreferences] = useState(() => readBuilderPreferences(browserBuilderPreferenceStorage()));
  const [viewport, setViewport] = useState<Viewport>(() => ({
    ...DEFAULT_VIEWPORT,
    w: initialPreferences.device.w,
    h: initialPreferences.device.h,
  }));
  // Reported up from the artboard's solver snapshot; shown as the toolbar's fit-to-content read-out.
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  // Where a chosen icon will land. Non-null IS the picker's open state — one source of truth, so a
  // dismissed dialog cannot leave a stale target behind for the next insert to reuse.
  const [iconTarget, setIconTarget] = useState<{ parentId: string; index: number } | null>(null);
  const uploadAsset = useUploadAsset(appId);
  const [outlines, setOutlines] = useState(initialPreferences.outlines);
  // The solver's rect for whatever is selected, lifted out of the artboard so the inspector can say
  // what `fill` and `hug` actually RESOLVED to. Nobody else can answer that: we own the solver, and
  // every other tool reads a number back out of the browser's layout.
  const [selectedFrame, setSelectedFrame] = useState<SolvedFrame | null>(null);
  const [activePanel, setActivePanel] = useState<BuilderPanelId>(initialPreferences.activePanel);
  const [focusedThemeToken, setFocusedThemeToken] = useState<{
    family: ThemeTokenFamily;
    token: string;
    request: number;
  } | null>(null);
  // F12 — zoom. A 768×1024 iPad artboard does not fit on a laptop beside three panels. Pure
  // presentation: a `transform: scale` on the artboard wrapper, which the artboard's `contain: paint`
  // was already written to compose with (see its comment).
  const [zoom, setZoom] = useState(initialPreferences.zoom);
  const [shortcutReferenceOpen, setShortcutReferenceOpen] = useState(false);
  const [componentPickerOpen, setComponentPickerOpen] = useState(false);
  const [clipboardReady, setClipboardReady] = useState(() => readInternalEditorClipboard() !== null);
  const [pendingPaste, setPendingPaste] = useState<{
    envelope: EditorClipboardEnvelope;
    parentId: string;
    index: number;
    summary: Extract<ReturnType<typeof planClipboardPaste>, { ok: true }>['summary'];
  } | null>(null);
  const componentPickerReturnFocus = useRef<HTMLElement | null>(null);
  // F9 — undo/redo are keyboard-only (⌘Z/⇧⌘Z) and nothing on screen says they exist, so a first-time
  // author cannot discover the one control that makes an editor safe to explore. Same store, now with
  // a visible, disable-aware button.
  const canUndo = useStore(session, (s) => s.undoStack.length > 0);
  const canRedo = useStore(session, (s) => s.redoStack.length > 0);
  const saveState = useAutosave(experienceId, appId, doc);
  const deliveryCampaign = campaigns?.find(
    (campaign) => campaign.experience_id === experienceId && campaign.environment_id === environmentId
  );
  const activeApp = orgs
    ?.flatMap((org) => org.workspaces.flatMap((workspace) => workspace.apps))
    .find((app) => app.id === appId);
  const activeEnvironment = activeApp?.environments.find((environment) => environment.id === environmentId);
  const platformLabel =
    activeApp?.platform === 'ios'
      ? 'iOS'
      : activeApp?.platform === 'android'
        ? 'Android'
        : activeApp?.platform
          ? activeApp.platform.replace('_', ' ')
          : 'Mobile';

  const step = findStep(doc, focusedStepId);
  // The active variant is DERIVED from focus (ADR 0015): the strip, the artboard and every node op
  // follow `focusedStepId`, so "switching variants" is just focusing a step in the other list.
  const activeKey = activeVariantKey(doc, focusedStepId);
  const activeSteps = stepsOf(doc, activeKey);
  const [variantDialogRequest, setVariantDialogRequest] = useState<VariantDialogRequest | null>(null);
  const resolvedAssets = useMemo(
    () => new Map((assets ?? []).map((asset) => [asset.id, { url: asset.url, contentType: asset.content_type }])),
    [assets]
  );
  const resolveAsset = useCallback((assetId: string) => resolvedAssets.get(assetId), [resolvedAssets]);
  const compiledStep = useMemo(() => {
    if (assetsPending) return { document: null, error: null };
    try {
      return {
        document: stepToExperienceDocument(doc, focusedStepId, resolveAsset),
        error: null,
      };
    } catch (error) {
      return { document: null, error: error instanceof Error ? error.message : 'Managed media could not be resolved.' };
    }
  }, [assetsPending, doc, focusedStepId, resolveAsset]);
  const assetMap = useMemo(
    () => Object.fromEntries((assets ?? []).map((asset) => [asset.id, asset.url])),
    [assets]
  );
  const insertionTarget = selectionAnchor && step ? findNode(step.root, selectionAnchor) : undefined;
  const componentPickerLabel = insertionTarget
    ? `Insert ${isContainer(insertionTarget.node) ? 'inside' : 'after'} ${nodeLabel(insertionTarget.node, doc.labels)}`
    : 'Insert at end of step';

  const changeComponentPickerOpen = useCallback((open: boolean): void => {
    if (open) {
      componentPickerReturnFocus.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
    setComponentPickerOpen(open);
    if (!open) {
      const target = componentPickerReturnFocus.current;
      window.requestAnimationFrame(() => {
        if (target?.isConnected) target.focus();
      });
    }
  }, []);

  const openThemeToken = useCallback((family: ThemeTokenFamily, token: string): void => {
    setActivePanel('theme');
    setFocusedThemeToken((current) => ({ family, token, request: (current?.request ?? 0) + 1 }));
  }, []);

  const insertAt = useCallback(
    (preset: BlockPreset, parentId: string, index: number) => {
      const { applyChange, select, doc: current, focusedStepId: stepId } = session.getState();
      const root = findStep(current, stepId)!.root;
      // The bound is enforced at save/publish; refuse at the point of action too, so an author never
      // builds past it and then loses the edit to a silent failed autosave (C4). The palette disables
      // itself in parallel; this also covers a drag dropped straight onto the canvas.
      if (docStats(root).nodes >= MAX_LAYOUT_NODES) return;
      // An icon needs a GLYPH before it is a publishable node, and choosing one is asynchronous (it
      // may rasterize and upload). Park the target and hand over to the picker; it comes back
      // through this same function with the asset resolved into `defaults`. Checked AFTER the node
      // bound so a full step refuses before opening a dialog the author cannot complete.
      if (preset.picks === 'icon') {
        setIconTarget({ parentId, index });
        return;
      }
      const { node, target } = materializePreset(preset, current.theme, root, { parentId, index });
      applyChange((draft) => insertNode(findStep(draft, stepId)!.root, target.parentId, target.index, node));
      select(node.id);
    },
    [session]
  );

  /** The picker's other half: a glyph, now a managed asset id, becomes the `icon` composition of
   *  SPEC §5.12 at the target `insertAt` parked. It re-enters `insertAt` rather than inserting
   *  directly so an icon gets the SAME node bound, undo checkpoint and selection as every other
   *  insert — and so a drag-dropped icon and a palette-clicked one cannot drift apart. */
  const chooseIcon = useCallback(
    (assetId: string) => {
      const target = iconTarget;
      if (!target) return;
      setIconTarget(null);
      insertAt(
        { label: 'Icon', kind: 'image', defaults: iconDefaults(assetId, session.getState().doc.theme) },
        target.parentId,
        target.index
      );
    },
    [iconTarget, insertAt, session]
  );

  const insert = useCallback(
    (preset: BlockPreset) => {
      const { doc: current, focusedStepId: stepId, selectionAnchor: selected } = session.getState();
      const { parentId, index } = insertionPoint(findStep(current, stepId)!.root, selected);
      insertAt(preset, parentId, index);
    },
    [insertAt, session]
  );

  const dragMove = useCallback(
    (id: string, parentId: string, index: number) => {
      const { applyChange, selectMany, doc: current, focusedStepId: stepId, selection: selected } = session.getState();
      const root = findStep(current, stepId)!.root;
      const hit = findNode(root, id);
      if (!hit?.parent) return;
      const ids = selected.includes(id) ? selected : [id];
      const normalized = normalizeTopLevelSelection(root, ids);
      try {
        applyChange((draft) => {
          moveNodes(findStep(draft, stepId)!.root, normalized, parentId, index);
        });
        selectMany(normalized, id);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Unable to move the selected components.');
      }
    },
    [session]
  );

  const remove = useCallback(() => {
    const { applyChange, doc: current, focusedStepId: stepId, selection: selected } = session.getState();
    const root = findStep(current, stepId)!.root;
    const ids = normalizeTopLevelSelection(root, selected).filter((id) => id !== root.id);
    if (ids.length === 0) return;
    try {
      applyChange((draft) => {
        removeNodes(findStep(draft, stepId)!.root, ids);
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to delete the selected components.');
    }
  }, [session]);

  // F2 — REORDER a block within its parent by ±1, reusing the fixture-tested `moveNode`. `moveNode`
  // reads its `index` in PRE-removal coordinates and subtracts 1 for a same-parent DOWNWARD move, so
  // a move down must target `to + 1` to net out to `to`. The edge guard is what keeps the first/last
  // child from wrapping.
  const reorder = useCallback(
    (id: string, delta: -1 | 1) => {
      const { applyChange, focusedStepId: stepId, doc: current, selection: selected } = session.getState();
      const root = findStep(current, stepId)!.root;
      const ids = normalizeTopLevelSelection(root, selected.includes(id) ? selected : [id]);
      const hits = ids.map((nodeId) => findNode(root, nodeId));
      const parent = hits[0]?.parent;
      if (!parent || hits.some((hit) => hit?.parent !== parent)) return;
      const indices = hits.map((hit) => hit!.index);
      const edge = delta < 0 ? Math.min(...indices) : Math.max(...indices);
      if ((delta < 0 && edge === 0) || (delta > 0 && edge === parent.children.length - 1)) return;
      const target = delta < 0 ? edge - 1 : edge + 2;
      applyChange((draft) => {
        moveNodes(findStep(draft, stepId)!.root, ids, parent.id, target);
      });
    },
    [session]
  );

  // F3 — DUPLICATE a block (fresh ids across the whole subtree) and select the copy.
  const duplicate = useCallback(
    (id: string) => {
      const { applyChange, selectMany, focusedStepId: stepId, doc: current, selection: selected } = session.getState();
      const root = findStep(current, stepId)!.root;
      const ids = normalizeTopLevelSelection(root, selected.includes(id) ? selected : [id]);
      const extraNodes = ids.reduce((sum, nodeId) => sum + docStats(findNode(root, nodeId)!.node).nodes, 0);
      if (docStats(root).nodes + extraNodes > MAX_LAYOUT_NODES) {
        toast.error(`Duplicating these components would exceed the ${MAX_LAYOUT_NODES}-node limit.`);
        return;
      }
      let cloneIds: string[] = [];
      applyChange((draft) => {
        cloneIds = duplicateNodes(findStep(draft, stepId)!.root, ids).map((node) => node.id);
      });
      if (cloneIds.length) selectMany(cloneIds, cloneIds[cloneIds.length - 1]);
    },
    [session]
  );

  const wrap = useCallback(
    (id: string, preset: BlockPreset) => {
      const { applyChange, select, focusedStepId: stepId, doc: current, selection: selected } = session.getState();
      const currentRoot = findStep(current, stepId)!.root;
      const ids = normalizeTopLevelSelection(currentRoot, selected.includes(id) ? selected : [id]);
      if (docStats(currentRoot).nodes >= MAX_LAYOUT_NODES) return;
      let wrapperId: string | null = null;
      try {
        applyChange((draft) => {
          const root = findStep(draft, stepId)!.root;
          // Eligibility is repeated at dispatch time: the menu may have been open while another
          // mutation changed bounds or removed the target.
          if (ids.length === 1 && !canWrapNode(root, id)) return;
          const { node } = materializePreset(preset, draft.theme, root, { parentId: root.id, index: 0 });
          if (!isContainer(node)) return;
          wrapperId = wrapNodes(root, ids, node).id;
        });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Unable to wrap the selected components.');
        return;
      }
      if (wrapperId) select(wrapperId);
    },
    [session]
  );

  const deleteNode = useCallback(
    (id: string) => {
      const { applyChange, doc: current, focusedStepId: stepId, selection: selected } = session.getState();
      const root = findStep(current, stepId)!.root;
      const ids = normalizeTopLevelSelection(root, selected.includes(id) ? selected : [id]).filter((nodeId) => nodeId !== root.id);
      if (ids.length) {
        try {
          applyChange((draft) => {
            removeNodes(findStep(draft, stepId)!.root, ids);
          });
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Unable to delete the selected components.');
        }
      }
    },
    [session]
  );

  const copySelection = useCallback(async (cut = false): Promise<void> => {
    const state = session.getState();
    const currentStep = findStep(state.doc, state.focusedStepId);
    if (!currentStep || !appId) return;
    const envelope = captureClipboardEnvelope({
      root: currentStep.root,
      ids: state.selection,
      labels: state.doc.labels,
      theme: state.doc.theme,
      source: { appId, experienceId, stepId: state.focusedStepId },
      assets: (assets ?? []).map((asset) => ({ id: asset.id, url: asset.url })),
    });
    if (!envelope) return;
    const transport = await writeEditorClipboard(envelope);
    setClipboardReady(true);
    if (cut) {
      // Clipboard permission may take long enough for selection or even the focused step to change.
      // Cut exactly the roots that were captured, against the live document, or do nothing if their
      // source step disappeared while the permission prompt was open.
      const copiedIds = envelope.roots.map((root) => root.id);
      const live = session.getState();
      if (findStep(live.doc, envelope.source.stepId)) {
        try {
          live.applyChange((draft) => {
            removeNodes(findStep(draft, envelope.source.stepId)!.root, copiedIds);
          });
        } catch (error) {
          toast.error('Copied, but the source components could not be removed.', {
            description: error instanceof Error ? error.message : undefined,
          });
          return;
        }
      }
    }
    toast.success(cut ? 'Cut components' : 'Copied components', {
      description: transport === 'internal' ? 'Available inside PersonalizeHQ; system clipboard access was unavailable.' : undefined,
    });
  }, [appId, assets, experienceId, session]);

  const commitPaste = useCallback((
    envelope: EditorClipboardEnvelope,
    parentId: string,
    index: number,
    reviewAccepted: boolean
  ): void => {
    const state = session.getState();
    if (!appId) return;
    const plan = planClipboardPaste({
      envelope,
      target: state.doc,
      appId,
      stepId: state.focusedStepId,
      parentId,
      index,
      availableAssetIds: new Set((assets ?? []).map((asset) => asset.id)),
      availableCampaignIds: new Set((campaigns ?? []).map((campaign) => campaign.id)),
    });
    if (!plan.ok) {
      toast.error('Paste refused', { description: plan.reasons.join(' ') });
      setPendingPaste(null);
      return;
    }
    const needsReview = plan.summary.importedTokens.length + plan.summary.renamedTokens.length + plan.summary.repairs.length > 0;
    if (needsReview && !reviewAccepted) {
      setPendingPaste({ envelope, parentId, index, summary: plan.summary });
      return;
    }
    state.applyChange((draft) => applyClipboardPaste(draft, state.focusedStepId, parentId, index, plan));
    state.selectMany(plan.roots.map((root) => root.id));
    setPendingPaste(null);
    toast.success(`Pasted ${plan.roots.length} component${plan.roots.length === 1 ? '' : 's'}.`);
  }, [appId, assets, campaigns, session]);

  const pasteSelection = useCallback(async (): Promise<void> => {
    const envelope = await readEditorClipboard();
    if (!envelope) {
      toast.error('Nothing from PersonalizeHQ is available to paste.');
      return;
    }
    const state = session.getState();
    const root = findStep(state.doc, state.focusedStepId)!.root;
    const target = insertionPoint(root, state.selectionAnchor);
    commitPaste(envelope, target.parentId, target.index, false);
  }, [commitPaste, session]);

  const requestInsert = useCallback(
    (id: string) => {
      componentPickerReturnFocus.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      session.getState().select(id);
      setComponentPickerOpen(true);
    },
    [session]
  );

  const nodeActions = useMemo(
    () => ({
      onInsert: requestInsert,
      onReorder: reorder,
      onDuplicate: duplicate,
      onWrap: wrap,
      onDelete: deleteNode,
      onCopy: (id: string) => {
        if (!session.getState().selection.includes(id)) session.getState().select(id);
        void copySelection(false);
      },
      onCut: (id: string) => {
        if (!session.getState().selection.includes(id)) session.getState().select(id);
        void copySelection(true);
      },
      onPaste: (id: string) => {
        if (!session.getState().selection.includes(id)) session.getState().select(id);
        void pasteSelection();
      },
    }),
    [requestInsert, reorder, duplicate, wrap, deleteNode, copySelection, pasteSelection, session]
  );

  useEffect(() => {
    writeBuilderPreferences(browserBuilderPreferenceStorage(), {
      zoom,
      outlines,
      activePanel,
      device: { w: viewport.w, h: viewport.h },
    });
  }, [activePanel, outlines, zoom, viewport.w, viewport.h]);

  const commandContext: EditorCommandContext = {
    surface: 'builder',
    pathname,
    navigate: (path) => void navigate({ to: path as '/home' }),
    canUndo,
    canRedo,
    canDelete: selection.some((id) => id !== step?.root.id),
    canZoomIn: zoom < 2,
    canZoomOut: zoom > 0.25,
    canDuplicate: selection.some((id) => id !== step?.root.id),
    canCopy: selection.some((id) => id !== step?.root.id),
    canCut: selection.some((id) => id !== step?.root.id),
    canPaste: clipboardReady,
    undo: () => session.getState().undo(),
    redo: () => session.getState().redo(),
    deleteSelection: remove,
    clearSelection: () => session.getState().select(null),
    selectParent: () => {
      const state = session.getState();
      const focused = findStep(state.doc, state.focusedStepId);
      const anchor = state.selectionAnchor;
      const hit = focused && anchor ? findNode(focused.root, anchor) : undefined;
      // `parent` is null for the step root (and for a node that no longer exists) — there is nothing
      // left to step out to, so clear back to step settings. Otherwise select the container.
      if (hit?.parent) state.select(hit.parent.id);
      else state.select(null);
    },
    suppressBrowserSave: () => {},
    zoomIn: () => setZoom((value) => Math.min(2, Math.round((value + 0.1) * 100) / 100)),
    zoomOut: () => setZoom((value) => Math.max(0.25, Math.round((value - 0.1) * 100) / 100)),
    resetZoom: () => setZoom(1),
    toggleOutlines: () => setOutlines((value) => !value),
    openShortcutReference: () => setShortcutReferenceOpen(true),
    duplicateSelection: () => { if (selectionAnchor) duplicate(selectionAnchor); },
    copySelection: () => { void copySelection(false); },
    cutSelection: () => { void copySelection(true); },
    pasteSelection: () => { void pasteSelection(); },
    activeBuilderPanel: activePanel,
    openBuilderPanel: setActivePanel,
    canCreateVariant: (doc.variants?.length ?? 0) < MAX_VARIANTS,
    canDeleteVariant: activeKey !== null,
    openCreateVariant: () => setVariantDialogRequest({ kind: 'create', nonce: Date.now() }),
    openDeleteVariant: () => setVariantDialogRequest({ kind: 'delete', nonce: Date.now() }),
  };
  const commandContextRef = useRef(commandContext);
  useEffect(() => {
    commandContextRef.current = commandContext;
  });
  const runCommand = (id: EditorCommandId): void => {
    runEditorCommand(id, commandContextRef.current);
  };

  // Capture keeps the open-layer guard ahead of React/Radix unmounts. Dispatch resolves command
  // availability against the ref's current editor state, never the state from when the effect ran.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      dispatchEditorKeydown(e, commandContextRef.current);
    };

    // CAPTURE, NOT BUBBLE — and this is the whole reason the guard above works.
    //
    // React attaches its listeners at the ROOT CONTAINER, which is inside `window`. So on the bubble
    // phase, React's handler — and, for a discrete event like a keypress, its synchronous re-render —
    // runs FIRST: Radix has already unmounted the open menu by the time a `window` listener looks for
    // it, and the guard finds nothing to guard against. Capturing puts us ahead of React, which is
    // the only moment the menu is still in the DOM to be seen.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  if (!step) return <div className="grid h-screen place-items-center">This document has no steps.</div>;

  return (
    <ThemeTokenNavigationProvider onOpen={openThemeToken}>
      <div className="flex h-screen flex-col bg-background text-[13px]">
      <header className="grid h-14 shrink-0 grid-cols-[220px_minmax(0,1fr)_auto] border-b bg-background xl:grid-cols-[248px_minmax(0,1fr)_auto] 2xl:grid-cols-[304px_minmax(0,1fr)_auto]">
        <div className="flex min-w-0 items-center gap-2 px-3">
          <Button variant="ghost" size="icon" asChild className="size-8 shrink-0 text-muted-foreground">
            <Link to="/experiences" aria-label="Back to experiences" title="Back to experiences">
              <ChevronLeft />
            </Link>
          </Button>
          <div className="min-w-0 leading-tight">
            <div className="flex min-w-0 items-center gap-1 text-[12px]">
              <Link to="/experiences" className="shrink-0 text-muted-foreground hover:text-foreground">
                Experiences
              </Link>
              <ChevronRight className="size-3 shrink-0 text-muted-foreground/50" />
              <span className="truncate font-medium">{name}</span>
            </div>
            <div className="flex min-w-0 items-center gap-1.5">
              <SaveBadge state={saveState} />
              <span
                aria-label={`Builder context: ${activeApp?.name ?? 'Unknown app'}, ${platformLabel}`}
                title={`${activeApp?.name ?? 'Unknown app'} · ${platformLabel}`}
                className="flex min-w-0 items-center gap-1 truncate text-[10px] text-muted-foreground"
              >
                <Smartphone className="size-2.5 shrink-0" />
                <span className="truncate">{activeApp?.name ?? 'App'} · {platformLabel}</span>
              </span>
              <span
                aria-label={`Environment: ${activeEnvironment?.name ?? 'unknown'}`}
                title={`Delivery environment: ${activeEnvironment?.name ?? 'unknown'}`}
                className={cn(
                  'shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
                  activeEnvironment?.name.toLocaleLowerCase() === 'production'
                    ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                    : 'border-border bg-muted text-muted-foreground'
                )}
              >
                {activeEnvironment?.name ?? 'unknown'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex min-w-0 items-center gap-2 overflow-x-auto px-3">
          <VariantSwitcher
            doc={doc}
            activeKey={activeKey}
            onSwitch={(key) => {
              const first = stepsOf(session.getState().doc, key)[0];
              if (first) session.getState().focusStep(first.id);
            }}
            onCreate={(variantName) => {
              // Capture the fresh key inside the draft, then land focus on the new arm's first step
              // — applyChange is synchronous, so the id is real by the next line.
              let firstStepId: string | undefined;
              session.getState().applyChange((draft) => {
                const variant = createVariant(draft, { name: variantName, fromKey: activeKey });
                firstStepId = variant.steps[0]?.id;
              });
              if (firstStepId) session.getState().focusStep(firstStepId);
            }}
            onRename={(key, variantName) =>
              session.getState().applyChange((draft) => renameVariant(draft, key, variantName))
            }
            onDelete={(key) => session.getState().applyChange((draft) => deleteVariant(draft, key))}
            dialogRequest={variantDialogRequest}
          />
          <Separator orientation="vertical" className="h-6" />
          <StepsStrip
            steps={activeSteps}
            focusedStepId={focusedStepId}
            onFocus={(id) => session.getState().focusStep(id)}
            onAdd={() =>
              session.getState().applyChange((draft) => {
                addStep(draft, undefined, {}, activeKey);
              })
            }
            onRemove={(id) => session.getState().applyChange((draft) => removeStep(draft, id))}
            onMove={(id, index) => session.getState().applyChange((draft) => moveStep(draft, id, index))}
            addDisabledReason={activeSteps.some((item) => item.surface === 'inline')
              ? 'Inline content is single-step. Convert it to an overlay surface before adding a step.'
              : undefined}
          />
        </div>

        <div className="flex items-center justify-end gap-2 whitespace-nowrap px-3">
          <div className="flex rounded-md bg-muted/60 p-0.5">
            <Button
              variant="ghost"
              size="icon"
              aria-label={commandById('builder.undo').label}
              title={`${commandById('builder.undo').label} (${formatKeybinding(commandById('builder.undo').keybindings[0]!)})`}
              disabled={!commandById('builder.undo').enabled(commandContext)}
              className="size-7 text-muted-foreground"
              onClick={() => runCommand('builder.undo')}
            >
              <Undo2 />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={commandById('builder.redo').label}
              title={`${commandById('builder.redo').label} (${formatKeybinding(commandById('builder.redo').keybindings[0]!)})`}
              disabled={!commandById('builder.redo').enabled(commandContext)}
              className="size-7 text-muted-foreground"
              onClick={() => runCommand('builder.redo')}
            >
              <Redo2 />
            </Button>
          </div>
          <Separator orientation="vertical" className="mx-1 h-5" />
          <CommandPalette context={commandContext} compact />
          <ShortcutReferenceButton onClick={() => runCommand('builder.shortcut-reference')} />
          <PreflightPanel
            doc={doc}
            resolveAsset={resolveAsset}
            onGo={(stepId, nodeId) => {
              const { focusStep, select } = session.getState();
              focusStep(stepId);
              // `focusStep` clears the selection, so the select must follow it, not race it.
              if (nodeId) select(nodeId);
            }}
          />
          <PreviewDialog
            experienceId={experienceId}
            variantKey={activeKey}
            variantName={doc.variants?.find((candidate) => candidate.key === activeKey)?.name ?? null}
          />
          <Button variant="default" size="sm" asChild className="shadow-none">
            <Link
              to={deliveryCampaign ? '/campaigns/$id' : '/campaigns'}
              {...(deliveryCampaign
                ? { params: { id: deliveryCampaign.id } }
                : { search: { experience: experienceId } })}
            >
              <Rocket className="size-4" />
              {deliveryCampaign ? 'Review & publish' : 'Set up delivery'}
            </Link>
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <BuilderSidebar
          activePanel={activePanel}
          onPanelChange={setActivePanel}
          layers={{
            root: step.root,
            labels: doc.labels,
            selection,
            selectionAnchor,
            onSelect: (id, mode) => session.getState().select(id, mode),
            onRename: (id, label) =>
              session.getState().applyChange((draft) => setLabel(draft, id, label), `${id}.label`),
            onInsert: requestInsert,
            onReorder: reorder,
            onDuplicate: duplicate,
            onWrap: wrap,
            onDelete: deleteNode,
            onCopy: nodeActions.onCopy,
            onCut: nodeActions.onCut,
            onPaste: nodeActions.onPaste,
            onMove: dragMove,
          }}
          theme={
            <ThemePanel
              key={focusedThemeToken ? `${focusedThemeToken.family}:${focusedThemeToken.token}:${focusedThemeToken.request}` : 'theme'}
              session={session}
              assets={assets ?? []}
              appTheme={appTheme}
              savingDefaults={saveAppTheme.isPending}
              focusedToken={focusedThemeToken}
              onSaveDefaults={(theme) =>
                saveAppTheme.mutate(theme, {
                  onSuccess: () => toast.success('Saved this experience theme as the app default.'),
                  onError: (error: Error) => toast.error(error.message),
                })
              }
            />
          }
          assets={
            <AssetsPanel
              appId={appId}
              assets={assets ?? []}
              loading={assetsPending}
              error={assetsFailed ? (assetsError instanceof Error ? assetsError.message : 'Try the request again.') : null}
              onRetry={() => void refetchAssets()}
              session={session}
            />
          }
        />

        <div className="relative min-w-0 flex-1">
          {/* The surround is dark in BOTH themes — the artboard pops like a phone in a dim room. */}
          <main className="absolute inset-0 overflow-auto bg-zinc-950">
            <div className="sticky left-0 top-0 z-10 flex h-14 min-w-full items-center justify-center px-3 pointer-events-none">
              <div
                aria-label="Canvas display controls"
                className="flex w-max max-w-full items-center gap-2 overflow-x-auto whitespace-nowrap rounded-md border bg-background/95 p-1 shadow-sm pointer-events-auto"
              >
                <ViewportToolbar viewport={viewport} onChange={setViewport} contentHeight={contentHeight} />
                <Separator orientation="vertical" className="mx-1 h-5" />
                <Button
                  variant="ghost"
                  size="sm"
                  aria-pressed={outlines}
                  title={commandById('builder.toggle-outlines').label}
                  className={outlines ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                  onClick={() => runCommand('builder.toggle-outlines')}
                >
                  <Frame />
                  Outlines
                </Button>
                <div className="flex items-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={commandById('builder.zoom-out').label}
                    title={`${commandById('builder.zoom-out').label} (${formatKeybinding(commandById('builder.zoom-out').keybindings[0]!)})`}
                    className="size-8 text-muted-foreground"
                    disabled={zoom <= 0.25}
                    onClick={() => runCommand('builder.zoom-out')}
                  >
                    <ZoomOut />
                  </Button>
                  <button
                    type="button"
                    aria-label="Reset zoom"
                    title="Reset zoom"
                    className="w-10 text-center text-[12px] tabular-nums text-muted-foreground hover:text-foreground"
                    onClick={() => runCommand('builder.reset-zoom')}
                  >
                    {Math.round(zoom * 100)}%
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={commandById('builder.zoom-in').label}
                    title={`${commandById('builder.zoom-in').label} (${formatKeybinding(commandById('builder.zoom-in').keybindings[0]!)})`}
                    className="size-8 text-muted-foreground"
                    disabled={zoom >= 2}
                    onClick={() => runCommand('builder.zoom-in')}
                  >
                    <ZoomIn />
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid min-h-[calc(100%_-_3.5rem)] min-w-full place-items-center p-4 pl-16 xl:p-6 xl:pl-20 2xl:p-8 2xl:pl-24">
              {/* F12: the outer box matches the scaled artboard so centering and scrollbars remain
                  correct even though CSS transforms do not affect layout. */}
              <div style={{ width: viewport.w * zoom, height: viewport.h * zoom }}>
                <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
                  {compiledStep.document ? <Artboard
                    doc={compiledStep.document}
                    assets={assetMap}
                    viewport={viewport}
                    selection={selection}
                    selectionAnchor={selectionAnchor}
                    outlines={outlines}
                    onSelect={(id, mode) => session.getState().select(id, mode)}
                    onMove={dragMove}
                    onInsert={insertAt}
                    actions={nodeActions}
                    labels={doc.labels}
                    inlineSlot={deliveryCampaign?.placement.kind === 'inline' ? deliveryCampaign.placement.slot : undefined}
                    hostPlatform={activeApp?.platform ?? undefined}
                    onSelectedFrame={setSelectedFrame}
                    onContentHeight={setContentHeight}
                  /> : (
                    <div role={compiledStep.error ? 'alert' : 'status'} className="grid h-full w-full place-items-center rounded-[inherit] bg-background p-6 text-center text-sm text-muted-foreground">
                      {compiledStep.error ?? 'Resolving managed media…'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </main>

          <div className="pointer-events-none absolute bottom-4 left-4 top-[4.5rem] z-20 flex items-center">
            <FloatingPalette
              onInsert={insert}
              pickerOpen={componentPickerOpen}
              onPickerOpenChange={changeComponentPickerOpen}
              pickerLabel={componentPickerLabel}
              canInsert={(preset) => {
                if (docStats(step.root).nodes >= MAX_LAYOUT_NODES) {
                  return `This step is at the ${MAX_LAYOUT_NODES}-node limit.`;
                }
                if (preset.requires === 'trackable_scroll' && trackableScroll(step.root) === null) {
                  return 'Add a carousel with at least one card first.';
                }
                return true;
              }}
            />
          </div>
        </div>

        <aside className="w-[240px] shrink-0 border-l bg-background xl:w-[280px] 2xl:w-[320px]">
          <Inspector session={session} selectedFrame={selectedFrame} />
        </aside>
      </div>
      <IconPicker
        open={iconTarget !== null}
        onOpenChange={(next) => {
          if (!next) setIconTarget(null);
        }}
        assets={assets ?? []}
        onUpload={(file) => uploadAsset.mutateAsync({ file })}
        onChoose={chooseIcon}
      />
      <ShortcutReference
        open={shortcutReferenceOpen}
        onOpenChange={setShortcutReferenceOpen}
        context={commandContext}
      />
      <PasteReviewDialog
        open={pendingPaste !== null}
        summary={pendingPaste?.summary ?? null}
        onCancel={() => setPendingPaste(null)}
        onConfirm={() => {
          if (pendingPaste) commitPaste(pendingPaste.envelope, pendingPaste.parentId, pendingPaste.index, true);
        }}
      />
      </div>
    </ThemeTokenNavigationProvider>
  );
}


const SAVE_LABEL: Record<SaveState, string> = {
  saved: 'Saved',
  dirty: 'Unsaved',
  saving: 'Saving…',
  error: 'Save failed',
};

function SaveBadge({ state }: { state: SaveState }): React.JSX.Element {
  return (
    <span
      className={`flex items-center gap-1.5 text-[11px] ${state === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}
    >
      <span
        className={`size-1.5 rounded-full ${
          state === 'error'
            ? 'bg-destructive'
            : state === 'dirty'
              ? 'bg-amber-500'
              : state === 'saving'
                ? 'animate-pulse bg-indigo-500'
                : 'bg-emerald-500'
        }`}
      />
      {SAVE_LABEL[state]}
    </span>
  );
}
